import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

// Local imports
import { ProductTableHeader } from './ProductTableHeader';
import { ProductTableRow } from './ProductTableRow';
import { CommentDialog } from './CommentDialog';
import {
  LABELS,
  UI_CONSTANTS
} from './AffectedProductsTab.constants';
import {
  filterProducts,
  getEmptyMessage
} from './AffectedProductsTab.utils';

export const AffectedProductsTab = ({
                                      affectedProducts,
                                      theme,
                                      selectedProductIds = [],
                                      onSelectProduct = () => {},
                                      plannedProductIds = [],
                                      readOnly = false
                                    }) => {
  // ================ Local State ================
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedComment, setSelectedComment] = useState("");
  const [brandFilter, setBrandFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');

  // Derive unique country names for filter dropdown
  const availableCountries = useMemo(() => {
    const countrySet = new Set();
    (affectedProducts || []).forEach(p => {
      (p.countries || []).forEach(c => countrySet.add(c.name || c));
    });
    return Array.from(countrySet).sort();
  }, [affectedProducts]);

  // ================ Handlers ================
  const handleOpenDialog = (comment) => {
    setSelectedComment(comment);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedComment("");
  };

  // ================ Data Processing ================
  const productsToDisplay = filterProducts(
      affectedProducts,
      readOnly,
      brandFilter,
      countryFilter
  );

  // ================ Main Render ================
  return (
      <Box sx={styles.container}>
        {/* Filter Controls */}
        <Box sx={styles.filterContainer}>
          {!readOnly && (
              <>
                <TextField
                    size="small"
                    label={LABELS.FILTER_BY_BRAND}
                    value={brandFilter}
                    onChange={(e) => setBrandFilter(e.target.value)}
                    sx={{ minWidth: 150, mr: 2 }}
                />
                <FormControl size="small" sx={{ minWidth: 160, mr: 2 }}>
                  <InputLabel>{LABELS.FILTER_BY_COUNTRY}</InputLabel>
                  <Select
                      value={countryFilter}
                      label={LABELS.FILTER_BY_COUNTRY}
                      onChange={(e) => setCountryFilter(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>All</em>
                    </MenuItem>
                    {availableCountries.map((name) => (
                        <MenuItem key={name} value={name}>{name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
          )}
        </Box>

        {/* Table Container */}
        <Box sx={styles.tableContainer(theme)}>
          {/* Table Header */}
          <ProductTableHeader theme={theme} />

          {/* Table Body */}
          <Box sx={styles.tableBody}>
            {productsToDisplay.length > 0 ? (
                productsToDisplay.map((product, index) => (
                    <ProductTableRow
                        key={product.productId}
                        product={product}
                        isSelected={selectedProductIds.includes(product.productId)}
                        isPlanned={plannedProductIds.includes(product.productId)}
                        readOnly={readOnly}
                        onSelectProduct={onSelectProduct}
                        onOpenComment={handleOpenDialog}
                        theme={theme}
                        isEven={index % 2 === 1}
                    />
                ))
            ) : (
                <EmptyState
                    theme={theme}
                    message={getEmptyMessage(
                        affectedProducts.length,
                        plannedProductIds.length
                    )}
                />
            )}
          </Box>
        </Box>

        {/* Comment Dialog */}
        <CommentDialog
            open={dialogOpen}
            onClose={handleCloseDialog}
            comment={selectedComment}
        />
      </Box>
  );
};

// ================ Empty State Component ================
const EmptyState = ({ theme, message }) => (
    <Box sx={styles.emptyState(theme)}>
      <Typography variant="body2">
        {message}
      </Typography>
    </Box>
);

// ================ Component Styles ================
const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'auto',
    minHeight: 0
  },
  filterContainer: {
    mb: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 1
  },
  tableContainer: (theme) => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 1,
    overflow: 'hidden',
    flex: 1,
    minHeight: 0,
    maxHeight: UI_CONSTANTS.MAX_HEIGHT
  }),
  tableBody: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    flex: 1,
    minHeight: 0,
    height: '100%'
  },
  emptyState: (theme) => ({
    p: 3,
    textAlign: 'center',
    color: theme.palette.text.disabled
  })
};